export type Gen5LayerStatus = 'ready' | 'partial' | 'blocked' | 'waiting';
export type Gen5RiskLevel = 'low' | 'medium' | 'high' | 'critical' | 'unknown';
export type Gen5ExecutionMode = 'autonomous' | 'approval_required' | 'audit_only' | 'manual' | 'unknown';

export interface Gen5LayerSummary {
  label: string;
  status: Gen5LayerStatus;
  summary?: string;
  confidence?: number;
}

export interface Gen5LayerStatusEnvelope {
  runId?: string;
  taskCategory?: string;
  riskLevel?: Gen5RiskLevel;
  executionMode?: Gen5ExecutionMode;
  layers: {
    context: Gen5LayerSummary;
    intelligence: Gen5LayerSummary;
    harness: Gen5LayerSummary;
  };
  context?: {
    sourceCount?: number;
    precedentCount?: number;
    evidenceCount?: number;
    matchedSignals?: string[];
  };
  approval?: {
    required?: boolean;
    status?: string;
    councilVerdict?: string;
  };
  replay?: {
    enabled?: boolean;
    mode?: string;
    baselineLabel?: string;
  };
  /** Gen7: Goal binding — links this action/run to a GoalContract */
  goalId?: string;
  goalIntent?: 'cash_velocity' | 'service_sla' | 'volume_growth' | 'profitability' | 'compliance_first' | 'customer_growth' | 'cost_reduction';
}

export interface Gen5LayerStatusCardProps {
  envelope: Gen5LayerStatusEnvelope;
  compact?: boolean;
  className?: string;
}

const STATUS_STYLE: Record<Gen5LayerStatus, string> = {
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  partial: 'border-amber-200 bg-amber-50 text-amber-800',
  blocked: 'border-red-200 bg-red-50 text-red-800',
  waiting: 'border-slate-200 bg-slate-50 text-slate-700',
};

const RISK_STYLE: Record<Gen5RiskLevel, string> = {
  low: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800',
  critical: 'bg-red-600 text-white',
  unknown: 'bg-slate-100 text-slate-700',
};

function formatConfidence(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  const normalized = value > 1 ? value : value * 100;
  return `${Math.round(normalized)}%`;
}

function labelForMode(mode?: Gen5ExecutionMode): string {
  if (mode === 'approval_required') return 'approval';
  if (mode === 'audit_only') return 'audit';
  if (mode === 'manual') return 'manual';
  if (mode === 'autonomous') return 'auto';
  return 'unknown';
}

function layerDotClass(status: Gen5LayerStatus): string {
  if (status === 'ready') return 'bg-emerald-500';
  if (status === 'partial') return 'bg-amber-500';
  if (status === 'blocked') return 'bg-red-500';
  return 'bg-slate-400';
}

function LayerPill({ layer }: { layer: Gen5LayerSummary }) {
  const confidence = formatConfidence(layer.confidence);
  return (
    <div className={`min-w-0 rounded-md border px-2 py-1.5 ${STATUS_STYLE[layer.status]}`}>
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${layerDotClass(layer.status)}`} />
        <span className="truncate text-[11px] font-semibold">{layer.label}</span>
        {confidence ? <span className="ml-auto shrink-0 text-[10px] opacity-80">{confidence}</span> : null}
      </div>
      {layer.summary ? <div className="mt-0.5 truncate text-[10px] opacity-80">{layer.summary}</div> : null}
    </div>
  );
}

export function Gen5LayerStatusCard({
  envelope,
  compact = false,
  className = '',
}: Gen5LayerStatusCardProps) {
  const risk = envelope.riskLevel ?? 'unknown';
  const modeLabel = labelForMode(envelope.executionMode);
  const matchedSignals = envelope.context?.matchedSignals?.slice(0, compact ? 2 : 4) ?? [];
  const counters = [
    { label: 'src', value: envelope.context?.sourceCount },
    { label: 'precedent', value: envelope.context?.precedentCount },
    { label: 'evidence', value: envelope.context?.evidenceCount },
  ].filter((item) => typeof item.value === 'number');

  return (
    <div className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${className}`.trim()}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-900">Gen5</span>
            {envelope.taskCategory ? (
              <span className="truncate rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                {envelope.taskCategory}
              </span>
            ) : null}
          </div>
          {envelope.runId ? <div className="mt-0.5 truncate text-[10px] text-slate-500">{envelope.runId}</div> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${RISK_STYLE[risk]}`}>{risk}</span>
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800">{modeLabel}</span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-1.5">
        <LayerPill layer={envelope.layers.context} />
        <LayerPill layer={envelope.layers.intelligence} />
        <LayerPill layer={envelope.layers.harness} />
      </div>

      {!compact && matchedSignals.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {matchedSignals.map((signal) => (
            <span key={signal} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
              {signal}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
        {counters.map((item) => (
          <span key={item.label}>
            {item.label} <span className="font-semibold text-slate-700">{item.value}</span>
          </span>
        ))}
        {envelope.approval?.required ? (
          <span className="font-semibold text-amber-700">{envelope.approval.status ?? 'approval required'}</span>
        ) : null}
        {envelope.approval?.councilVerdict ? (
          <span className="font-semibold text-blue-700">{envelope.approval.councilVerdict}</span>
        ) : null}
        {envelope.replay?.enabled ? (
          <span className="font-semibold text-slate-700">replay {envelope.replay.mode ?? 'snapshot'}</span>
        ) : null}
      </div>
    </div>
  );
}
